// Web Serial API wrapper for SM25/SM15 fingerprint sensor
// Handles connection, buffered reads, and writes over USB-to-serial bridges

// Supported USB-to-serial board identifiers:
// CAMA original board — CH341 chip (QinHeng Electronics)
// 0x7523 = CH340/CH341 USB-to-serial, 0x5523 = CH341 in serial mode
const CH341_VENDOR_ID = 0x1a86;
const CH341_PRODUCT_IDS = [0x7523, 0x5523];
// Olimex ESP-Prog board — FTDI FT2232HL chip
const FTDI_VENDOR_ID = 0x0403;
const FTDI_PRODUCT_ID = 0x6010;

// Error codes thrown by this layer (detected by UI for i18n)
export const SERIAL_ERR_PORT_OPEN_FAILED = 'SERIAL_ERR_PORT_OPEN_FAILED';
export const SERIAL_ERR_PORT_BUSY = 'SERIAL_ERR_PORT_BUSY';

export class Sm25Serial {
	constructor() {
		this.port = null;
		this.reader = null;
		this.writer = null;
		this.buffer = new Uint8Array(0);
		this.onDisconnect = null;
		this.disconnectHandler = null;
	}

	get isConnected() {
		return this.port !== null && this.reader !== null;
	}

	setDisconnectCallback(cb) {
		this.onDisconnect = cb;
	}

	async connect() {
		if (this.port) {
			throw new Error('Already connected');
		}

		// Show CAMA (CH341) and Olimex ESP-Prog (FTDI) boards in the picker
		this.port = await navigator.serial.requestPort({
			filters: [
				...CH341_PRODUCT_IDS.map(pid => ({ usbVendorId: CH341_VENDOR_ID, usbProductId: pid })),
				{ usbVendorId: FTDI_VENDOR_ID, usbProductId: FTDI_PRODUCT_ID },
			],
		});

		try {
			await this.port.open({
				baudRate: 115200,
				dataBits: 8,
				stopBits: 1,
				parity: 'none',
			});
		} catch (err) {
			this.port = null;
			const msg = err instanceof Error ? err.message : '';
			if (msg.includes('Failed to open serial port')) {
				throw new Error(SERIAL_ERR_PORT_OPEN_FAILED);
			}
			throw err;
		}

		// CH341 requires DTR and RTS asserted for data flow
		await this.port.setSignals({
			dataTerminalReady: true,
			requestToSend: true,
		});

		if (!this.port.readable || !this.port.writable) {
			throw new Error('Port is not readable/writable');
		}

		this.reader = this.port.readable.getReader();
		this.writer = this.port.writable.getWriter();
		this.buffer = new Uint8Array(0);

		// Monitor for physical disconnection
		this.disconnectHandler = () => {
			this._cleanupOnDisconnect();
			if (this.onDisconnect) this.onDisconnect();
		};
		this.port.addEventListener('disconnect', this.disconnectHandler);
	}

	_cleanupOnDisconnect() {
		try { if (this.reader) this.reader.releaseLock(); } catch (e) { /* ignore */ }
		try { if (this.writer) this.writer.releaseLock(); } catch (e) { /* ignore */ }
		this.reader = null;
		this.writer = null;
		this.port = null;
		this.disconnectHandler = null;
		this.buffer = new Uint8Array(0);
	}

	async disconnect() {
		if (this.port && this.disconnectHandler) {
			this.port.removeEventListener('disconnect', this.disconnectHandler);
			this.disconnectHandler = null;
		}

		try {
			if (this.reader) {
				await this.reader.cancel();
				this.reader.releaseLock();
				this.reader = null;
			}
		} catch (e) {
			// Ignore reader cleanup errors
		}

		try {
			if (this.writer) {
				this.writer.releaseLock();
				this.writer = null;
			}
		} catch (e) {
			// Ignore writer cleanup errors
		}

		try {
			if (this.port) {
				await this.port.close();
				this.port = null;
			}
		} catch (e) {
			// Ignore port close errors
		}

		this.buffer = new Uint8Array(0);
	}

	async write(data) {
		if (!this.writer) {
			throw new Error('Not connected');
		}
		await this.writer.write(data);
	}

	/**
	 * Read exactly `length` bytes from the serial port, using an internal buffer
	 * to handle partial USB reads.
	 */
	async readExact(length, timeoutMs) {
		if (!this.reader) {
			throw new Error('Not connected');
		}

		const deadline = Date.now() + timeoutMs;
		const result = new Uint8Array(length);
		let pos = 0;

		// Drain from buffer first
		if (this.buffer.length > 0) {
			const take = Math.min(this.buffer.length, length);
			result.set(this.buffer.subarray(0, take), 0);
			this.buffer = this.buffer.subarray(take);
			pos = take;
		}

		while (pos < length) {
			const remaining = deadline - Date.now();
			if (remaining <= 0) {
				throw new Error('Timeout reading from sensor');
			}

			const readPromise = this.reader.read();
			let timeoutHandle;
			const timeoutPromise = new Promise((_, reject) => {
				timeoutHandle = setTimeout(
					() => reject(new Error('Timeout reading from sensor')),
					remaining
				);
			});

			let value;
			let done;
			try {
				({ value, done } = await Promise.race([readPromise, timeoutPromise]));
			} catch (err) {
				readPromise
					.then(({ value: lateValue, done: lateDone }) => {
						if (!lateDone && lateValue && lateValue.length) {
							const merged = new Uint8Array(
								this.buffer.length + lateValue.length
							);
							merged.set(this.buffer, 0);
							merged.set(lateValue, this.buffer.length);
							this.buffer = merged;
						}
					})
					.catch(() => {});
				throw err;
			} finally {
				clearTimeout(timeoutHandle);
			}

			if (done || !value) {
				throw new Error('Serial port stream ended');
			}

			const needed = length - pos;
			if (value.length <= needed) {
				result.set(value, pos);
				pos += value.length;
			} else {
				result.set(value.subarray(0, needed), pos);
				this.buffer = value.subarray(needed);
				pos += needed;
			}
		}

		return result;
	}
}
